/**
 * Auth headers for requests to the CMHKAgentPlatform Gateway.
 *
 *   X-Id-Token      Federate id_token — the credential the Gateway authenticates.
 *   X-Access-Token  Federate access_token — carried alongside so the Gateway can
 *                   forward or store it for downstream use. Optional: omitted when
 *                   no live access token is in its cookie. The Gateway never uses
 *                   it to establish identity.
 *
 * Every Gateway call site (GatewayClient, the SSE paths in ChatPanel, the
 * CopilotKit AG-UI transport, ApiTestPage) builds its headers here so the two
 * tokens cannot drift apart. Do NOT use this for other backends (Permission
 * Service, IVT Hub, Eval, Schedule): they have their own header contracts and
 * CORS allowlists that do not admit X-Access-Token.
 */

import { FEDERATE } from '../config';
import { getIdToken } from './AuthContext';
import { decodeJwt, getCookie } from './authStorage';

export const ID_TOKEN_HEADER = 'X-Id-Token';
export const ACCESS_TOKEN_HEADER = 'X-Access-Token';

/**
 * Current Federate access_token, or undefined when absent or expired.
 *
 * The cookie is written by accessTokenLoop.storeAccessToken with the token's own
 * expiry, so the browser normally drops it on time; the JWT `exp` check is a
 * belt-and-braces guard for a clock that moved. Opaque (non-JWT) tokens are
 * returned as-is — the cookie lifetime is their only expiry signal.
 */
export function getAccessToken(): string | undefined {
  const token = getCookie(FEDERATE.accessTokenKey);
  if (!token) return undefined;
  try {
    const { exp } = decodeJwt(token);
    if (typeof exp === 'number' && exp * 1000 <= Date.now()) return undefined;
  } catch {
    /* opaque token */
  }
  return token;
}

/**
 * Build the auth headers for one Gateway request, merged over `base`
 * (Content-Type, Accept, ...). Each token is added only when present.
 */
export function gatewayAuthHeaders(base: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { ...base };
  const idToken = getIdToken();
  if (idToken) headers[ID_TOKEN_HEADER] = idToken;
  const accessToken = getAccessToken();
  if (accessToken) headers[ACCESS_TOKEN_HEADER] = accessToken;
  return headers;
}
